// src/contexts/AlertContext.tsx
//
// Error-surfacing decision rule (this app has three overlapping primitives — pick
// by scope, not habit):
//   - Field/form-level validation or a single failed submit -> components/ApiErrorMessage.tsx
//     (extractApiErrorMessage + inline alert next to the form).
//   - A whole page/section crashed (ErrorBoundary, 403/500) -> error/ErrorStateCard.tsx
//     via the /standard-error, /access-error pages.
//   - Connectivity changed (browser online/offline) -> components/network/NetworkStatusToast.tsx,
//     not this context — do not call showGlobalAlert for connectivity, see axiosConfig.ts.
//   - A background action needs a transient, non-blocking heads-up (e.g. "export
//     failed") -> react-toastify's toast.error(...).
//   - This banner (showGlobalAlert) is reserved for app-wide, non-connectivity server
//     conditions (e.g. the dev-only 500 notice in axiosConfig.ts) that don't fit the above.
import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';

type AlertType = 'danger' | 'warning' | 'success' | 'info';

const AUTO_HIDE_MS = 8000;

interface AlertMessage {
  type: AlertType;
  message: string;
}

interface AlertContextType {
  showAlert: (type: AlertType, message: string) => void;
  hideAlert: () => void;
  alert: AlertMessage | null;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Global alert callback that can be called from anywhere (including non-React code)
let globalShowAlert: ((type: AlertType, message: string) => void) | null = null;

export const showGlobalAlert = (type: AlertType, message: string) => {
  if (globalShowAlert) {
    globalShowAlert(type, message);
  }
};

const DangerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
    style={{ marginRight: 8, flexShrink: 0 }}
  >
    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
  </svg>
);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [alert, setAlert] = useState<AlertMessage | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideAlert = useCallback(() => {
    setAlert(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showAlert = useCallback((type: AlertType, message: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setAlert({ type, message });
    timerRef.current = setTimeout(() => {
      setAlert(null);
      timerRef.current = null;
    }, AUTO_HIDE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Register the global callback
  useEffect(() => {
    globalShowAlert = showAlert;
    return () => {
      globalShowAlert = null;
    };
  }, [showAlert]);

  return (
    <AlertContext.Provider
      value={useMemo(
        () => ({ showAlert, hideAlert, alert }),
        [showAlert, hideAlert, alert]
      )}
    >
      {alert && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            minWidth: '300px',
            maxWidth: '500px',
          }}
        >
          <Alert variant={alert.type} onClose={hideAlert} dismissible>
            {alert.type === 'danger' && <DangerIcon />}
            {alert.message}
          </Alert>
        </div>
      )}
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
